import { LoggingService } from "../loggingSvc";
import * as Entity from "./entity-contracts";
import { Observable, Subject, BehaviorSubject } from "@reactivex/rxjs";

/** Implementation of Entity Store, allowing consumers of the store to store entity specific items
 * and allow changes to the entity to be observed.
 */
export class EntityStore<T> implements Entity.Store<T> {
  private items: Map<string, EntityValueContainer<T>> = new Map<string, EntityValueContainer<T>>();
  private storeChangeSubject: Subject<Entity.StoreChangeDetails>;

  // @ngInject
  constructor(public readonly entityType: string, private loggingService: LoggingService) {
    this.storeChangeSubject = new Subject<Entity.StoreChangeDetails>();
  }

  public getChangesAsObservable(): Observable<Entity.StoreChangeDetails> {
    return this.storeChangeSubject.asObservable();
  }

  /**
   * Provides an item store for a given key, creates a store item if not present
   * @param {string} key - Key for a store Item.
   * @param valueContext context for the value.
   * @param reason for creating value context
   * @param fetchDataCallback function which is called to get the data from external service.
   * @param forceUpdate updates the fetchDataCallBack method
   */
  public getOrAddValueContainer(
    key: string,
    valueContext?: Entity.ValueContext<T>,
    reason?: string,
    fetchDataCallback?: any,
    forceUpdate?: boolean
  ): Entity.StoreValueContainer<T> {
    // value container is not present in the entity store
    if (!this.items.has(key)) {
      // check if valueContext already exists to be used
      if (!valueContext) {
        valueContext = this.createValueContext(reason, fetchDataCallback);
      }

      this.items.set(key, new EntityValueContainer<T>(this, key, this.loggingService, valueContext));
    } else {
      if (forceUpdate) {
        let valueContainer = this.items.get(key);
        valueContainer.setValue(this.createValueContext(reason, fetchDataCallback, forceUpdate));
      }
    }

    return this.items.get(key);
  }

  /**
   * Set value to providing details about the change.
   * @param reason reason for the change.
   * @param value value that triggered change.
   * @param error error triggered the change.
   */
  public setValue(reason: string, value: any, error?: any) {
    this.storeChangeSubject.next({
      reason: reason,
      entityType: this.entityType,
      data: value,
      error: error
    });
  }

  private createValueContext(reason: string, fetchDataCallback?: any, forceUpdate?: boolean): Entity.ValueContext<T> {
    let valueContext: Entity.ValueContext<T> = {
      reason: reason,
      valueObservable: undefined
    };

    if (fetchDataCallback) {
      valueContext.valueObservable = forceUpdate
        ? () => Observable.fromPromise(fetchDataCallback(forceUpdate))
        : () => Observable.fromPromise(fetchDataCallback());
    }

    return valueContext;
  }
}

/** Implementation of StoreItem allows entity data to be stored and provide api for changes to be observed*/
export class EntityValueContainer<T> implements Entity.StoreValueContainer<T> {
  private valueSubject: BehaviorSubject<T>;
  private pending: boolean;
  private lastUpdatedDate: Date;
  public readonly additionalProperties: Map<string, string> = new Map<string, string>();
  // @ngInject
  constructor(
    private store: EntityStore<T>,
    public readonly key: string,
    private loggingService: LoggingService,
    private defaultValue?: Entity.ValueContext<T>
  ) {
    this.pending = false;
    this.valueSubject = new BehaviorSubject<T>(undefined);
    if (this.defaultValue) {
      this.setValue(this.defaultValue);
    }
  }

  private get value(): T {
    return this.valueSubject.value && angular.copy(this.valueSubject.value);
  }

  /** get entity type associated to the item */
  public get entityType(): string {
    return this.store.entityType;
  }

  /** Last value was updated */
  public get lastUpdated(): Date {
    return this.lastUpdatedDate;
  }

  /** entity value as observable */
  public asObservable(): Observable<T> {
    return this.valueSubject
      .asObservable()
      .filter((data: any) => data != undefined && !this.pending)
      .map((data: any) => angular.copy(data));
  }

  /**Set the value based on the value observable */
  public setValue(context: Entity.ValueContext<T>) {
    if (!this.pending) {
      this.pending = true;
      let valueObservable = context.valueObservable();
      valueObservable.subscribe(
        (data: T) => this.next(context.reason, data),
        (error: any) => this.error(context.reason, error)
      );
    } else {
      this.loggingService.logInfo({
        entityType: this.entityType,
        reason: context.reason,
        key: this.key,
        message: "set value called while pending is true"
      });
    }
  }

  /** Allows to set new value to item stream */
  private next(reason: string, data: T): void {
    this.pending = false;
    this.lastUpdatedDate = new Date();
    this.valueSubject.next(data);
    this.store.setValue(reason, data);
  }

  /** Allows to set new error value to error stream */
  private error(reason: string, error: any): void {
    this.pending = false;
    let valueSubjectInError = this.valueSubject;
    this.valueSubject = new BehaviorSubject<T>(undefined);
    valueSubjectInError.error(error);
    this.store.setValue(reason, this.value, error);
  }
}
